import {
    collection,
    CollectionReference,
    doc,
    getDoc,
    getDocs,
    orderBy,
    query,
    setDoc,
    Timestamp,
    where
} from "firebase/firestore";
import FirebaseService from "./firebaseService";
import {Collection} from "../constants/Collection";
import {ICheckrResult} from "../types/ICheckrResult";
import {IBackgroundCheck} from "../types/IBackgroundCheck";
import {IUserModel} from "../types/UserModel";
import userService from "./userService";
import {SkipAdminStatus} from "../constants/SkipAdminStatus";
import {sendUserNotification} from "../utils/notificationUtils";
import {NotificationType} from "../constants/NotificationType";

export interface IBgcUser extends IUserModel {
    candidateId?: string;
    adverseActionDate?: Timestamp;
}

class BgcService {
    checkrRef: CollectionReference;
    bgcRef: CollectionReference;

    constructor() {
        const db = FirebaseService.db;
        this.checkrRef = collection(db, Collection.CHECKR_RESULTS);
        this.bgcRef = collection(db, Collection.BACKGROUND_CHECK);
    }

    async getCheckrResult(userId: string): Promise<ICheckrResult | undefined> {
        const snap = await getDoc(doc(this.checkrRef, userId));
        if (snap.exists()) {
            return {id: snap.id, ...snap.data()} as ICheckrResult;
        }
        return undefined;
    }

    async getBackgroundCheck(userId: string): Promise<IBackgroundCheck | undefined> {
        const snap = await getDoc(doc(this.bgcRef, userId));
        if (snap.exists()) {
            return {id: snap.id, ...snap.data()} as IBackgroundCheck;
        }
        return undefined;
    }

    async setSkipAdminStatus(userId: string, status: SkipAdminStatus, adverseActionDate?: Date): Promise<void> {
        const ref = doc(FirebaseService.db, Collection.BACKGROUND_CHECK, userId);
        const existing = await getDoc(ref);
        const data: Record<string, unknown> = {skipAdminStatus: status};
        if (adverseActionDate) {
            data.adverseActionDate = Timestamp.fromDate(adverseActionDate);
        }
        await setDoc(ref, data, {merge: true});

        const considerManuallyCleared = status === SkipAdminStatus.APPROVED && existing.data()?.report_status === 'consider';
        if (considerManuallyCleared) {
            await this.sendApprovalNotification(userId);
        }
    }

    async sendApprovalNotification(userId: string): Promise<void> {
        await sendUserNotification(userId, {
            title: 'Background check approved 🎉',
            message: 'You\'re good to go. Open Skip to keep things moving!',
            data: {type: NotificationType.BACKGROUND_CHECK_APPROVED}
        });
    }

    async getNewBgcReviews(): Promise<IBgcUser[]> {
        // Get all consider results ordered by saved_at ascending (oldest first)
        const snap = await getDocs(
            query(this.checkrRef, where('report_status', '==', 'consider'), orderBy('saved_at', 'asc'))
        );

        const checkrResults: ICheckrResult[] = snap.docs
            .filter(d => d.exists())
            .map(d => ({id: d.id, ...d.data()} as ICheckrResult));

        // Fetch BackgroundCheck docs in parallel
        const bgcDocs = await Promise.all(
            checkrResults.map(r => this.getBackgroundCheck(r.id))
        );

        // Only keep users with no skipAdminStatus (not yet marked)
        const filteredResults = checkrResults.filter((_, idx) => {
            const bgc = bgcDocs[idx];
            return !bgc?.skipAdminStatus;
        });

        const users = await Promise.all(filteredResults.map(r => userService.getUser(r.id)));
        return filteredResults
            .map((checkr, idx) => {
                const user = users[idx];
                if (!user) return undefined;
                return {...user, candidateId: checkr.candidate_id ?? undefined} as IBgcUser;
            })
            .filter((u): u is IBgcUser => u !== undefined);
    }

    async getWaitingWindowBgcReviews(): Promise<IBgcUser[]> {
        // Get users marked as REVIEWED, ordered by adverseActionDate ascending (soonest first)
        const snap = await getDocs(
            query(this.bgcRef, where('skipAdminStatus', '==', SkipAdminStatus.REVIEWED), orderBy('adverseActionDate', 'asc'))
        );

        const bgcDocs = snap.docs
            .filter(d => d.exists())
            .map(d => ({id: d.id, ...d.data()} as IBackgroundCheck));

        const [users, checkrResults] = await Promise.all([
            Promise.all(bgcDocs.map(d => userService.getUser(d.id))),
            Promise.all(bgcDocs.map(d => this.getCheckrResult(d.id)))
        ]);

        return bgcDocs
            .map((bgcDoc, idx) => {
                const user = users[idx];
                if (!user) return undefined;
                return {
                    ...user,
                    candidateId: checkrResults[idx]?.candidate_id ?? undefined,
                    adverseActionDate: bgcDoc.adverseActionDate
                } as IBgcUser;
            })
            .filter((u): u is IBgcUser => u !== undefined);
    }
}

export default new BgcService();
